import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  Analytics as AnalyticsIcon,
  LocalOffer as BadgeIcon,
  QrCode2 as BatchIcon,
  PrecisionManufacturing as BomIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Chat as ChatIcon,
  ChevronLeft as ChevronLeftIcon,
  Assessment as CountingIcon,
  SupportAgent as CRMIcon,
  Dashboard as DashboardIcon,
  AccountBalanceWallet as FinanceIcon,
  Flag as FlagIcon,
  MonitorHeart as HealthIcon,
  Sync as IntegrationIcon,
  Download as DownloadIcon,
  Tune as ConnectorSettingsIcon,
  Factory as ManufacturingIcon,
  Category as MaterialIcon,
  Menu as MenuIcon,
  BarChart as MetricsIcon,
  People as PeopleIcon,
  Inventory2 as ProductsIcon,
  ReceiptLong as PurchasesIcon,
  VerifiedUser as QCIcon,
  Security as RolesIcon,
  QrCode as SerialIcon,
  Settings as SettingsIcon,
  ShoppingCart as ShoppingCartIcon,
  Storage as StorageIcon,
  LocalShipping as SupplierIcon,
  HeadsetMic as SupportChatIcon,
  TrackChanges as TrackChangesIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { useFeatureFlagsContext } from '@shared/hooks/useFeatureFlag';

const DRAWER_WIDTH_FULL = 260;
const DRAWER_WIDTH_MINI = 72;

// Sidebar modes: 'full' | 'mini' | 'hidden'
export default function AdminSidebar({
  mobile = false,
  onNavigate,
  mode = 'full',
  onModeChange,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { user } = useAuth();
  const { features } = useFeatureFlagsContext();

  const isSuperAdmin = user?.role === 'super_admin';

  const isMini = mode === 'mini';
  const isHidden = mode === 'hidden';
  const drawerWidth = isMini ? DRAWER_WIDTH_MINI : DRAWER_WIDTH_FULL;

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const handleToggle = () => {
    if (mode === 'full') {
      onModeChange?.('mini');
    } else if (mode === 'mini') {
      onModeChange?.('hidden');
    } else {
      onModeChange?.('full');
    }
  };

  const handleExpand = () => {
    onModeChange?.('full');
  };

  const menuItems = [
    // === Core (always visible) ===
    { text: t('nav.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('nav.users'), icon: <PeopleIcon />, path: '/users' },
    { text: t('nav.roles'), icon: <RolesIcon />, path: '/roles', flag: 'rbac' },
    { text: t('nav.products'), icon: <ProductsIcon />, path: '/products' },
    {
      text: t('nav.categories'),
      icon: <CategoryIcon />,
      path: '/categories',
    },
    { text: t('nav.badges'), icon: <BadgeIcon />, path: '/products/badges', flag: 'product_badges' },
    {
      text: t('nav.inventory'),
      icon: <WarehouseIcon />,
      path: '/inventory',
    },
    {
      text: t('nav.counting'),
      icon: <CountingIcon />,
      path: '/inventory-counting',
      flag: 'inventory_counting',
    },
    {
      text: t('nav.batches'),
      icon: <BatchIcon />,
      path: '/batches',
      flag: 'batch_tracking',
    },
    { text: t('nav.serials'), icon: <SerialIcon />, path: '/serials', flag: 'serial_numbers' },
    { text: t('nav.orders'), icon: <ShoppingCartIcon />, path: '/orders' },
    { text: t('nav.purchases'), icon: <PurchasesIcon />, path: '/purchases', flag: 'purchases_module' },
    {
      text: t('nav.bom'),
      icon: <BomIcon />,
      path: '/manufacturing/bom',
      flag: 'bill_of_materials',
    },
    {
      text: t('nav.manufacturing'),
      icon: <ManufacturingIcon />,
      path: '/manufacturing',
      flag: 'manufacturing_module',
    },
    {
      text: t('nav.production'),
      icon: <TrackChangesIcon />,
      path: '/production',
      flag: 'production_tracker',
    },
    { text: t('nav.materials'), icon: <MaterialIcon />, path: '/materials', flag: 'materials_module' },
    { text: t('nav.qualityControl'), icon: <QCIcon />, path: '/qc', flag: 'quality_control' },
    { text: 'CRM', icon: <CRMIcon />, path: '/crm', flag: 'crm_module' },
    { text: t('nav.finance'), icon: <FinanceIcon />, path: '/finance', flag: 'finance_module' },
    { text: t('nav.messenger'), icon: <ChatIcon />, path: '/chat', flag: 'messenger_module' },
    {
      text: t('nav.customerSupport'),
      icon: <SupportChatIcon />,
      path: '/customer-support',
      flag: 'customer_support',
    },
    { text: t('nav.integrations'), icon: <IntegrationIcon />, path: '/integrations', flag: 'integrations_module' },
    { text: t('nav.suppliers'), icon: <SupplierIcon />, path: '/suppliers', flag: 'suppliers_module' },
    { text: t('nav.analytics'), icon: <AnalyticsIcon />, path: '/analytics' },
    {
      text: t('nav.advancedAnalytics'),
      icon: <AnalyticsIcon />,
      path: '/analytics/advanced',
      flag: 'advanced_analytics',
    },
    // Super Admin only - Organizations management
    ...(isSuperAdmin
      ? [
          {
            text: 'Super Admin',
            icon: <AdminPanelSettingsIcon />,
            path: '/super-admin',
            dividerBefore: true,
          },
          {
            text: t('nav.featureFlags'),
            icon: <FlagIcon />,
            path: '/super-admin/feature-flags',
          },
          {
            text: t('nav.onecDatabases'),
            icon: <StorageIcon />,
            path: '/super-admin/1c-databases',
          },
          {
            text: t('nav.onecMetrics'),
            icon: <MetricsIcon />,
            path: '/super-admin/1c-metrics',
          },
          {
            text: t('nav.onecHealth'),
            icon: <HealthIcon />,
            path: '/super-admin/1c-health',
          },
          {
            text: t('nav.onecSyncLogs'),
            icon: <IntegrationIcon />,
            path: '/super-admin/1c-sync-logs',
          },
          {
            text: t('nav.onecInstaller'),
            icon: <DownloadIcon />,
            path: '/integrations/1c-installer',
          },
          {
            text: t('nav.connectorSettings'),
            icon: <ConnectorSettingsIcon />,
            path: '/integrations/connector-settings',
          },
          {
            text: t('nav.organizations'),
            icon: <BusinessIcon />,
            path: '/organizations',
          },
        ]
      : []),
    { text: t('nav.settings'), icon: <SettingsIcon />, path: '/settings' },
  ];

  // Filter menu items by feature flags
  const visibleItems = menuItems.filter(
    (item) => !item.flag || features?.[item.flag]?.enabled
  );

  // Hidden state - show only expand button
  if (isHidden && !mobile) {
    return (
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: 48,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1,
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer,
        }}
      >
        <IconButton
          onClick={handleExpand}
          sx={{ minWidth: 44, minHeight: 44 }}
          aria-label={t('common.openMenu')}
        >
          <MenuIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: mobile ? DRAWER_WIDTH_FULL : drawerWidth,
        height: '100%',
        bgcolor: 'background.paper',
        borderRight: mobile ? 0 : '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease-in-out',
        overflow: 'hidden',
      }}
    >
      <Toolbar
        sx={{
          justifyContent: isMini ? 'center' : 'space-between',
          px: isMini ? 0 : 2,
        }}
      >
        {!isMini && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: isSuperAdmin ? 'warning.main' : 'inherit',
            }}
          >
            {isSuperAdmin ? 'SUPER ADMIN' : t('nav.adminPanel')}
          </Typography>
        )}
        {!mobile && (
          <IconButton
            onClick={handleToggle}
            size="small"
            sx={{ minWidth: 44, minHeight: 44 }}
            aria-label={isMini ? t('common.hideMenu') : t('common.collapseMenu')}
          >
            {isMini ? <ChevronLeftIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Toolbar>
      <Divider />

      <List sx={{ overflow: 'auto', flex: 1, py: 0.5 }}>
        {visibleItems.map((item) => {
          const isSelected = location.pathname === item.path;
          const button = (
            <ListItemButton
              selected={isSelected}
              onClick={() => go(item.path)}
              sx={{
                minHeight: 48,
                justifyContent: isMini ? 'center' : 'flex-start',
                px: isMini ? 1 : 2.5,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  '& .MuiListItemIcon-root': { color: 'white' },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isMini ? 0 : 2,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isMini && <ListItemText primary={item.text} />}
            </ListItemButton>
          );

          return (
            <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
              {isMini ? (
                <Tooltip title={item.text} placement="right" arrow>
                  {button}
                </Tooltip>
              ) : (
                button
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}

export {
  DRAWER_WIDTH_FULL as ADMIN_DRAWER_WIDTH,
  DRAWER_WIDTH_MINI as ADMIN_DRAWER_WIDTH_MINI,
};
